import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

import { AccountService } from './account.service';
import { Account, Faculty } from '../models';

@Injectable()
export class Principal {
    private userIdentity: Account;
    private authenticated = false;
    private authenticationState = new Subject<any>();

    constructor(private accountService: AccountService) {
    }

    public authenticate(identity: Account) {
        this.userIdentity = identity;
        this.authenticated = identity !== null;
        this.authenticationState.next(this.userIdentity);
    }

    public hasAnyAuthority(authorities: string[]): Promise<boolean> {
        return Promise.resolve(this.hasAnyAuthorityDirect(authorities));
    }

    public hasAnyAuthorityDirect(authorities: string[]): boolean {
        if (!this.authenticated || !this.userIdentity || !this.userIdentity.authorities) {
            return false;
        }

        for (let i = 0; i < authorities.length; i++) {
            if (this.userIdentity.authorities.indexOf(authorities[i]) !== -1) {
                return true;
            }
        }

        return false;
    }

    public hasAuthority(authority: string): Promise<boolean> {
        if (!this.authenticated) {
            return Promise.resolve(false);
        }

        return this.identity().then((id: any) => {
            return Promise.resolve(id.authorities && id.authorities.indexOf(authority) !== -1);
        }, () => {
            return Promise.resolve(false);
        });
    }

    public identity(force?: boolean): Promise<any> {
        if (force === true) {
            this.userIdentity = undefined;
        }

        // check and see if we have retrieved the userIdentity data from the server.
        // if we have, reuse it by immediately resolving
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity);
        }

        // retrieve the userIdentity data from the server, update the identity object, and then resolve.
        return this.accountService.get().toPromise().then((account: Account) => {
            if (account) {
                this.userIdentity = account;
                this.authenticated = true;
            } else {
                this.userIdentity = null;
                this.authenticated = false;
            }
            this.authenticationState.next(this.userIdentity);
            return this.userIdentity;
        }).catch((error) => {
            this.userIdentity = null;
            this.authenticated = false;
            this.authenticationState.next(this.userIdentity);
            return null;
        });
    }

    public isAuthenticated(): boolean {
        return this.authenticated;
    }

    public isIdentityResolved(): boolean {
        return this.userIdentity !== undefined;
    }

    public getAuthenticationState(): Observable<any> {
        return this.authenticationState.asObservable();
    }

    public getImageUrl(): String {
        return this.isIdentityResolved() ? this.userIdentity.imageUrl : null;
    }

    public getUserFirstName(): String {
        return this.isIdentityResolved() ? this.userIdentity.firstName : null;
    }

    public getUserLastName(): String {
        return this.isIdentityResolved() ? this.userIdentity.lastName : null;
    }

    public userFaculties(): Observable<Array<Faculty>> {
        return this.accountService.userFaculties();
    }

    public hasAccessToFaculty(value: number | Faculty): boolean {
        if (!(value instanceof Faculty) && !(typeof value === 'number')) {
            throw {
                name: 'ArgumentTypeError',
                message: 'Argument should have type number or Faculty.'
            };
        }

        const facultyId: number = (typeof value === 'number') ? <number>value : (<Faculty>value).id;
        return this.userIdentity.faculties.some((userFacultyId: number) => facultyId === userFacultyId);
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/services/principal.service.ts